/**
 * Zod-free quotation detection helpers, shared by the intake UI (client) and the
 * reclassify route (server). Kept in its own module — separate from
 * `quotations.ts`, which imports zod — so the client can value-import
 * `looksLikeVendorQuotation` without dragging zod into the SPA bundle (HS2-D7PNHT).
 */

/**
 * The document kinds a supporting upload can turn out to really be that the
 * quote-extract pipeline (Flow F3) knows how to ingest as vendor quotes.
 * A supporting doc classified as one of these is offered as "add it as a quote".
 */
export const QUOTATION_PROPOSED_KINDS = ['quotation', 'comparison-sheet'] as const;

/**
 * Detection predicate (HS2-FKJB32) shared by the intake UI and the reclassify
 * route: a document uploaded as `supporting` "looks like a vendor quotation"
 * when the classifier flagged a kind mismatch and proposed a quote-shaped kind
 * (`quotation` or `comparison-sheet`). Both callers pass the same document
 * shape — the client `IntakeDoc` and the server `documents` row line up on
 * `kind` + `extractionResult.{proposedKind,kindMismatch}`.
 */
export function looksLikeVendorQuotation(doc: {
  kind?: string | null;
  extractionResult?: { proposedKind?: string | null; kindMismatch?: boolean | null } | null;
}): boolean {
  if (doc.kind !== 'supporting') return false;
  const result = doc.extractionResult;
  if (result === null || result === undefined) return false;
  if (result.kindMismatch !== true) return false;
  const proposed = result.proposedKind ?? undefined;
  return proposed !== undefined && (QUOTATION_PROPOSED_KINDS as readonly string[]).includes(proposed);
}
